'use client'

import { useEffect, useMemo, useRef, type FormEvent } from 'react'
import { Button } from '@acko/button'
import { OtpInput } from '@acko/otp-input'
import { Typography } from '@acko/typography'
import {
  ABHA_HERO_MODAL_MOBILE_OTP_STEP_DATA,
  ABHA_HERO_MODAL_OTP_STEP_DATA,
  ABHA_HERO_MODAL_OTP_DISCLAIMER,
  ABHA_HERO_MODAL_DISCLAIMER_ICON,
  ABHA_HERO_MODAL_RESEND_OTP,
  ABHA_HERO_MODAL_RESEND_OTP_COUNTDOWN_LABEL,
} from '@components/NewProduct/constants'
import { ModalDisclaimer } from '@components/NewProduct/AbhaHeroWidget/ModalDisclaimer'
import { formatOtpResendCountdown } from '@components/NewProduct/AbhaHeroWidget/steps/formatOtpResendCountdown'
import {
  formatAbhaHeroStepTemplate,
  resolveAbhaHeroStepData,
} from '@components/NewProduct/AbhaHeroWidget/steps/abhaHeroStepData'
import { ABHA_OTP_LENGTH } from '@helpers/abhaWidgetHelper'

export type AbhaHeroOtpStepContent = {
  title: string
  description: string
  disclaimerText: string
  disclaimerIconSrc: string
  otpLabel: string
  ctaText: string
}

export type AbhaHeroOtpStepProps = {
  data?: Record<string, unknown>
  isMobile: boolean
  maskedPhoneNumber?: string
  phoneNumber?: string
  variant?: 'aadhaar' | 'mobile'
  otp: string
  otpError: string
  submitError: string
  prepareError: string
  resendCountdown: number
  isVerifyOtpEnabled: boolean
  isSubmitting: boolean
  canResendOtp: boolean
  onOtpChange: (value: string) => void
  onResendOtp: () => void
  onSubmit: (event: FormEvent<HTMLFormElement>) => void
}

const OTP_STEP_DEFAULTS: AbhaHeroOtpStepContent = {
  title: ABHA_HERO_MODAL_OTP_STEP_DATA.TITLE,
  description: ABHA_HERO_MODAL_OTP_STEP_DATA.DESCRIPTION,
  disclaimerText: ABHA_HERO_MODAL_OTP_DISCLAIMER,
  disclaimerIconSrc: ABHA_HERO_MODAL_DISCLAIMER_ICON,
  otpLabel: ABHA_HERO_MODAL_OTP_STEP_DATA.OTP_LABEL,
  ctaText: ABHA_HERO_MODAL_OTP_STEP_DATA.CTA_TEXT,
}

const MOBILE_OTP_STEP_DEFAULTS: AbhaHeroOtpStepContent = {
  title: ABHA_HERO_MODAL_MOBILE_OTP_STEP_DATA.TITLE,
  description: ABHA_HERO_MODAL_MOBILE_OTP_STEP_DATA.DESCRIPTION,
  disclaimerText: '',
  disclaimerIconSrc: ABHA_HERO_MODAL_DISCLAIMER_ICON,
  otpLabel: ABHA_HERO_MODAL_MOBILE_OTP_STEP_DATA.OTP_LABEL,
  ctaText: ABHA_HERO_MODAL_MOBILE_OTP_STEP_DATA.CTA_TEXT,
}

export const AbhaHeroOtpStep = ({
  data,
  isMobile,
  maskedPhoneNumber,
  phoneNumber,
  variant = 'aadhaar',
  otp,
  otpError,
  submitError,
  prepareError,
  resendCountdown,
  isVerifyOtpEnabled,
  isSubmitting,
  canResendOtp,
  onOtpChange,
  onResendOtp,
  onSubmit,
}: AbhaHeroOtpStepProps) => {
  const otpInputRef = useRef<HTMLDivElement>(null)
  const stepData = useMemo(() => {
    const defaults = variant === 'mobile' ? MOBILE_OTP_STEP_DEFAULTS : OTP_STEP_DEFAULTS
    const resolvedData = resolveAbhaHeroStepData(data, isMobile, defaults)

    return {
      ...resolvedData,
      description: formatAbhaHeroStepTemplate(resolvedData.description, {
        masked_phone_number: maskedPhoneNumber,
        phone_number: phoneNumber,
      }),
    }
  }, [data, isMobile, maskedPhoneNumber, phoneNumber, variant])
  const descriptionParagraphs = stepData.description.split(/\n{2,}/).filter(Boolean)

  useEffect(() => {
    const focusTimerId = window.setTimeout(() => {
      const firstOtpCell = otpInputRef.current?.querySelector<HTMLInputElement>('.acko-otp-cell')

      firstOtpCell?.focus()
    }, 0)

    return () => window.clearTimeout(focusTimerId)
  }, [])

  return (
    <form className="abha-hero-modal__content abha-hero-modal__content--form" onSubmit={onSubmit}>
      <div className="abha-hero-modal__scroll">
        <div className="abha-hero-modal__intro">
          <Typography variant="heading-lg" as="h2" color="primary" className="abha-hero-modal__intro-title">
            {stepData.title}
          </Typography>
          {descriptionParagraphs.map((description, index) => (
            <Typography
              key={index}
              scale="sm"
              emphasis="normal"
              color="secondary"
              className="abha-hero-modal__intro-description">
              {description}
            </Typography>
          ))}
        </div>

        <div className="abha-hero-modal__otp-block">
          <OtpInput
            ref={otpInputRef}
            className="abha-hero-modal__otp"
            label={stepData.otpLabel}
            value={otp}
            length={ABHA_OTP_LENGTH}
            size={isMobile ? 'sm' : 'lg'}
            error={Boolean(otpError)}
            errorText={otpError || undefined}
            onChange={onOtpChange}
          />

          {canResendOtp ? (
            <button type="button" className="abha-hero-modal__resend" onClick={onResendOtp}>
              {ABHA_HERO_MODAL_RESEND_OTP}
            </button>
          ) : (
            <Typography as="p" scale="xs" emphasis="normal" className="abha-hero-modal__resend-countdown">
              {`${ABHA_HERO_MODAL_RESEND_OTP_COUNTDOWN_LABEL} ${formatOtpResendCountdown(resendCountdown)}`}
            </Typography>
          )}
        </div>

        {submitError && (
          <Typography scale="xs" emphasis="normal" color="error">
            {submitError}
          </Typography>
        )}

        {prepareError && (
          <Typography scale="xs" emphasis="normal" color="error">
            {prepareError}
          </Typography>
        )}
      </div>

      <div className="abha-hero-modal__footer">
        {stepData.disclaimerText && (
          <div className="abha-hero-modal__footer-disclaimer">
            <ModalDisclaimer text={stepData.disclaimerText} iconSrc={stepData.disclaimerIconSrc} />
          </div>
        )}

        <div className="abha-hero-modal__footer-action">
          <Button
            type="submit"
            variant="primary"
            size="lg"
            fullWidth
            disabled={!isVerifyOtpEnabled}
            loading={isSubmitting}>
            {stepData.ctaText}
          </Button>
        </div>
      </div>
    </form>
  )
}
